/**
 * Utilitários para validação do vídeo de confirmação (3s) no cadastro médico.
 *
 * A duração alvo é 3s, com tolerância técnica de ±0.5s para variação de
 * encoding/browser. Não usar igualdade estrita em float.
 */

export const CONFIRMATION_VIDEO_TARGET_SECONDS = 3;
export const CONFIRMATION_VIDEO_MIN_SECONDS = 2.5;
export const CONFIRMATION_VIDEO_MAX_SECONDS = 3.5;
export const CONFIRMATION_VIDEO_MAX_BYTES = 20 * 1024 * 1024;
export const CONFIRMATION_VIDEO_DURATION_READ_TIMEOUT_MS = 8_000;

export const CONFIRMATION_VIDEO_INVALID_FORMAT_ERROR =
  'Formato inválido. Envie um vídeo MP4 ou WEBM.';
export const CONFIRMATION_VIDEO_TOO_LARGE_ERROR =
  'O vídeo de confirmação deve ter no máximo 20MB.';
export const CONFIRMATION_VIDEO_UNREADABLE_DURATION_ERROR =
  'Não foi possível ler a duração do vídeo. Envie um MP4 ou WEBM válido com cerca de 3 segundos.';

const recordedDurationByFile = new WeakMap<File, number>();

export function markConfirmationVideoRecordedDuration(
  file: File,
  durationSeconds: number,
): void {
  if (Number.isFinite(durationSeconds) && durationSeconds > 0) {
    recordedDurationByFile.set(file, durationSeconds);
  }
}

export function getConfirmationVideoRecordedDuration(file: File): number | undefined {
  return recordedDurationByFile.get(file);
}

export function normalizeConfirmationVideoMime(mimeType?: string | null): string {
  return (mimeType || '').split(';')[0].trim().toLowerCase();
}

export function isAllowedConfirmationVideoMime(mimeType?: string | null): boolean {
  const mime = normalizeConfirmationVideoMime(mimeType);
  return mime === 'video/mp4' || mime === 'video/webm' || mime === 'video/quicktime';
}

export function isAllowedConfirmationVideoFile(file: Pick<File, 'name' | 'type'>): boolean {
  const mimeOk = isAllowedConfirmationVideoMime(file.type);
  const extOk = /\.(mp4|webm|mov)$/i.test(file.name || '');
  return mimeOk || extOk;
}

export function isValidConfirmationVideoDuration(durationSeconds: number): boolean {
  return (
    Number.isFinite(durationSeconds) &&
    durationSeconds >= CONFIRMATION_VIDEO_MIN_SECONDS &&
    durationSeconds <= CONFIRMATION_VIDEO_MAX_SECONDS
  );
}

export function formatConfirmationVideoDurationError(duration?: number | null): string {
  if (duration == null || !Number.isFinite(duration)) {
    return CONFIRMATION_VIDEO_UNREADABLE_DURATION_ERROR;
  }
  return `O vídeo de confirmação deve ter cerca de 3 segundos (aceitamos entre ${CONFIRMATION_VIDEO_MIN_SECONDS}s e ${CONFIRMATION_VIDEO_MAX_SECONDS}s). Duração detectada: ${duration.toFixed(1)}s.`;
}

function isConfirmationVideoDebugEnabled(): boolean {
  return typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.DEV;
}

function logConfirmationVideoDebug(stage: string, payload: Record<string, unknown>): void {
  if (!isConfirmationVideoDebugEnabled()) return;
  console.debug(`[confirmation-video] ${stage}`, payload);
}

function isPositiveFiniteDuration(duration: number): boolean {
  return Number.isFinite(duration) && duration > 0;
}

/**
 * Chrome/Chromium MediaRecorder gera WebM sem Duration no container.
 * `loadedmetadata` dispara com `duration === Infinity`; um seek forçado
 * faz o browser calcular a duração real.
 */
function resolveDurationFromVideoElement(
  video: HTMLVideoElement,
  onResolved: (duration: number) => void,
): void {
  if (isPositiveFiniteDuration(video.duration)) {
    onResolved(video.duration);
    return;
  }

  const tryAccept = () => {
    if (isPositiveFiniteDuration(video.duration)) {
      onResolved(video.duration);
      return true;
    }
    return false;
  };

  video.addEventListener('seeked', tryAccept);
  video.addEventListener('timeupdate', tryAccept);
  video.addEventListener('durationchange', tryAccept);

  try {
    video.currentTime = Number.MAX_SAFE_INTEGER;
  } catch {
    tryAccept();
  }
}

/**
 * Lê a duração de um File de vídeo no browser via elemento <video>.
 */
export function readVideoDurationSeconds(file: File): Promise<number> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.muted = true;
    video.playsInline = true;
    video.setAttribute('playsinline', 'true');

    let settled = false;
    const timeoutId = window.setTimeout(() => {
      finish(null);
    }, CONFIRMATION_VIDEO_DURATION_READ_TIMEOUT_MS);

    const cleanup = () => {
      window.clearTimeout(timeoutId);
      video.onloadedmetadata = null;
      video.onerror = null;
      video.removeAttribute('src');
      URL.revokeObjectURL(url);
    };

    const finish = (duration: number | null) => {
      if (settled) return;
      settled = true;
      cleanup();
      if (duration == null || !isPositiveFiniteDuration(duration)) {
        reject(new Error(CONFIRMATION_VIDEO_UNREADABLE_DURATION_ERROR));
        return;
      }
      resolve(duration);
    };

    video.onloadedmetadata = () => {
      resolveDurationFromVideoElement(video, (duration) => finish(duration));
    };

    video.onerror = () => {
      finish(null);
    };

    video.src = url;
  });
}

export async function validateConfirmationVideoFile(
  file: File,
): Promise<{ valid: true; duration: number } | { valid: false; error: string }> {
  const mime = normalizeConfirmationVideoMime(file.type);
  logConfirmationVideoDebug('validate:start', {
    name: file.name,
    mime: file.type,
    normalizedMime: mime,
    size: file.size,
  });

  if (file.size > CONFIRMATION_VIDEO_MAX_BYTES) {
    logConfirmationVideoDebug('validate:reject', { reason: 'too-large', size: file.size });
    return { valid: false, error: CONFIRMATION_VIDEO_TOO_LARGE_ERROR };
  }

  if (!isAllowedConfirmationVideoFile(file)) {
    logConfirmationVideoDebug('validate:reject', { reason: 'invalid-format', mime: file.type });
    return { valid: false, error: CONFIRMATION_VIDEO_INVALID_FORMAT_ERROR };
  }

  const recordedDuration = getConfirmationVideoRecordedDuration(file);
  let duration: number | null = null;
  let durationSource: 'metadata' | 'recorded-fallback' | 'unreadable' = 'unreadable';

  try {
    duration = await readVideoDurationSeconds(file);
    durationSource = 'metadata';
  } catch {
    duration = null;
  }

  if (
    duration == null &&
    recordedDuration != null &&
    isValidConfirmationVideoDuration(recordedDuration)
  ) {
    duration = recordedDuration;
    durationSource = 'recorded-fallback';
  }

  logConfirmationVideoDebug('validate:duration', {
    duration,
    durationSource,
    recordedDuration,
    mime: file.type,
    size: file.size,
  });

  if (duration == null || !Number.isFinite(duration)) {
    return { valid: false, error: CONFIRMATION_VIDEO_UNREADABLE_DURATION_ERROR };
  }

  if (!isValidConfirmationVideoDuration(duration)) {
    return { valid: false, error: formatConfirmationVideoDurationError(duration) };
  }

  logConfirmationVideoDebug('validate:accept', { duration, durationSource });
  return { valid: true, duration };
}
